/**
 * 流式编排核心运行时工具。
 *
 * <p>从 useStreamOrchestrator 抽出的不依赖具体流式入口的公共动作：</p>
 * <ul>
 *   <li>{@link finalizeAssistantTurn} - 把服务端最终 done payload 同步到消息列表</li>
 *   <li>{@link settleStream} - 流结束收尾（摘除注册表登记 / markEnded / titlePending）</li>
 *   <li>{@link buildStreamBridge} - 拼装 ChatStreamBridge（注入 store 状态给 streamEvents 派发器）</li>
 *   <li>{@link cancelStream} - 用户主动停止生成</li>
 *   <li>{@link markAssistantPending} - 把消息列表中某个 assistant 消息置为 pending 态</li>
 *   <li>{@link handleStreamError} - 统一的异常分支处理（用户取消/网络错误）</li>
 * </ul>
 */
import {
  formatSseStreamError,
  type AiChatResponse
} from '@/api/aiChat'
import { useAiSessionsStore } from '@/stores/aiSessions'
import { cancelSseV2Stream } from '@/api/aiChatV2'
import { resolveAssistantVisibleText } from '@/utils/aiMessageDisplay'
import { markRaw, ref } from 'vue'
import {
  dispatchChatStreamEvent,
  type ChatStreamBridge
} from './streamEvents'
import { buildCompletedTurnView, syncFinalAssistantText } from './timeline'
import type { AiChatContext, CancellableAbort, StreamRegistry } from './context'
import { sessionHasOpenUserAction } from './sessionTask'
import {
  genMessageId,
  type AiUiMessage,
  type ChatStreamAccumulator
} from './types'

/** finalizeAssistantTurn 所需的最小依赖（避免直接依赖整个 AiChatContext）。 */
export interface FinalizeDeps {
  messages: AiChatContext['messages']
}

/**
 * 断连后运行被安全停止（recoverable 恒为终态，后端不再有可重连的活流）时的用户引导：
 * 进度已落库，正确的续接方式是回答待办卡或发送“继续”开启新一轮，
 * 而不是提供一个点了只会重复探测终态的“恢复”按钮。
 */
export const STREAM_INTERRUPTED_CONTINUE_HINT =
  '与 AI 服务的连接中断，任务已安全停止，生成进度已保存。如有未回答的提问卡请直接作答；否则发送「继续」即可从断点恢复任务。'

/**
 * Patch the session that owns an in-flight stream, even if the user is viewing another thread.
 */
export function withOwnerMessages(
  messages: AiChatContext['messages'],
  messagesBySession: Map<string, AiUiMessage[]>,
  currentSessionId: string | null,
  ownerSessionId: string | null | undefined,
  fn: (target: AiChatContext['messages']) => void
): void {
  const owner = (ownerSessionId ?? currentSessionId ?? '').trim()
  const viewing = !owner || (currentSessionId?.trim() ?? '') === owner
  if (viewing) {
    fn(messages)
    return
  }
  const cached = ref(messagesBySession.get(owner) ?? [])
  fn(cached)
  messagesBySession.set(owner, cached.value)
}

/** 并发判断辅助：用户当前是否正停留在该会话上（跨会话流写入的依据）。 */
export function isViewingSession(
  sessionId: AiChatContext['sessionId'],
  ownerSessionId: string | null | undefined
): boolean {
  const owner = (ownerSessionId ?? '').trim()
  if (!owner) {
    return true
  }
  return (sessionId.value?.trim() ?? '') === owner
}

/** Bind the viewed session to a completed stream only if the user is still on it. */
export function bindViewSessionIfCurrent(
  sessionId: AiChatContext['sessionId'],
  ownerSessionId: string
): void {
  if ((sessionId.value?.trim() ?? '') === ownerSessionId.trim()) {
    sessionId.value = ownerSessionId
  }
}

/**
 * 把服务端最终 done payload 同步到消息列表（pending 关闭 + 同步 timeline）。
 * 抽出来供 sendMessage / regenerate / V2 待办恢复共用。
 *
 * COMPLETED 走折叠构建（buildCompletedTurnView）：主体只留最后一轮回复，过程进抽屉，
 * 与 run_done 实时派发、刷新后的服务端合并视图（messages.ts）三方同契约。
 */
export function finalizeAssistantTurn(
  deps: FinalizeDeps,
  pendingMsgId: string,
  data: AiChatResponse
): void {
  const idx = deps.messages.value.findIndex((m) => m.id === pendingMsgId)
  if (idx < 0) {
    return
  }
  const prevTurn = deps.messages.value[idx]
  const visibleReply = resolveAssistantVisibleText(data.assistantMessage || '')
  // 折叠仅在有权威终稿时进行（与 streamEvents run_done 同口径）：空终稿退回原展示。
  if (data.finishReason === 'COMPLETED' && visibleReply.trim().length > 0) {
    const view = buildCompletedTurnView(prevTurn.timeline ?? undefined, data.assistantMessage || '', data.durationMs ?? null)
    deps.messages.value.splice(idx, 1, {
      ...prevTurn,
      content: view.content || prevTurn.content,
      reasoningContent: data.reasoningContent ?? prevTurn.reasoningContent ?? null,
      timeline: view.timeline,
      collapsedPriorRounds: view.collapsedPriorRounds,
      runStatus: view.collapsedPriorRounds ? 'normal' : null,
      pending: false,
      error: null
    })
    return
  }
  const timeline = syncFinalAssistantText(prevTurn.timeline ?? undefined, visibleReply)
  if (timeline.length > 0) {
    // 性能债修复(热点2)：时间线为整体读取的展示数据，标记原始跳过深度代理。
    markRaw(timeline)
  }
  deps.messages.value.splice(idx, 1, {
    ...prevTurn,
    content: visibleReply || prevTurn.content,
    reasoningContent: data.reasoningContent ?? prevTurn.reasoningContent ?? null,
    timeline: timeline.length > 0 ? timeline : null,
    pending: false,
    error: null
  })
}

/** settleStream 所需的最小依赖。 */
export interface SettleDeps {
  streamsBySession: StreamRegistry
  userActionQueue: AiChatContext['userActionQueue']
}

/**
 * 收尾工作：从注册表移除该会话的流 + 通知 sessionsStore 结束 + 清理 titlePending。
 * sendMessage / regenerate / 待办恢复均在各自 finally 中调用本函数。
 *
 * <p>并发放开后流按会话分桶：收尾只摘除 owner 会话自己的登记，不影响其他
 * 在途流。awaitingUserAction / sending 由主 store 按当前会话派生，无需在此清理。</p>
 *
 * <p>若该会话仍有确认卡/提问卡，任务语义上未结束（与输入框
 * {@code sending || awaitingUserAction} 同构），侧边栏转圈保持。</p>
 */
export function settleStream(
  deps: SettleDeps,
  ownerSessionId: string | null | undefined,
  sessionsStore: ReturnType<typeof useAiSessionsStore>
): void {
  const owner = (ownerSessionId ?? '').trim()
  if (!owner) {
    return
  }
  deps.streamsBySession.unregister(owner)
  // 流结束且没有待办：立刻把侧边栏转动圆圈切回删除按钮；
  // 不等后端 SSE run_state 事件，避免用户点停止后 UI 残留。
  if (!sessionHasOpenUserAction(owner, deps.userActionQueue.value)) {
    sessionsStore.markEnded(owner)
  }
  const s = sessionsStore.sessions.find((x) => x.id === owner)
  if (s?.titlePending) {
    sessionsStore.abandonSessionTitleGenerating(owner)
  }
}

/** buildStreamBridge 所需的最小依赖。 */
export interface BridgeDeps {
  messages: AiChatContext['messages']
  sessionId: AiChatContext['sessionId']
  userActionQueue: AiChatContext['userActionQueue']
  lastError: AiChatContext['lastError']
  resumableStop: AiChatContext['resumableStop']
  streamsBySession: StreamRegistry
  aiPlanStore: AiChatContext['aiPlanStore']
  messagesBySession: AiChatContext['messagesBySession']
}

/** buildStreamBridge 的入参。 */
export interface BuildStreamBridgeParams {
  pendingMsgId: string
  pendingMsgTemplate?: AiUiMessage
  sessionsStore: ReturnType<typeof useAiSessionsStore>
  hadPersistedSession: boolean
  abortCtrl: CancellableAbort
  acc: ChatStreamAccumulator
  clientUserMsgId?: string | null
}

/**
 * 拼装 ChatStreamBridge：把 store 的所有响应式状态与派发器需要的辅助函数集中打包。
 * 主对话、重新生成和待办恢复入口共用同一份 bridge 形态。
 */
export function buildStreamBridge(
  deps: BridgeDeps,
  params: BuildStreamBridgeParams
): ChatStreamBridge {
  return {
    pendingMsgId: params.pendingMsgId,
    pendingMsgTemplate: params.pendingMsgTemplate,
    messages: deps.messages,
    sessionId: deps.sessionId,
    userActionQueue: deps.userActionQueue,
    lastError: deps.lastError,
    resumableStop: deps.resumableStop,
    sessionsStore: params.sessionsStore,
    planStore: deps.aiPlanStore,
    hadPersistedSession: params.hadPersistedSession,
    abortCtrl: params.abortCtrl,
    acc: params.acc,
    clientUserMsgId: params.clientUserMsgId,
    messagesBySession: deps.messagesBySession,
    streamsBySession: deps.streamsBySession
  }
}

/** 把指定 id 的 assistant 消息置为 pending（用于流开始时把宿主气泡切回加载态）。 */
export function markAssistantPending(
  messages: AiChatContext['messages'],
  msgId: string
): void {
  const idx = messages.value.findIndex((m) => m.id === msgId)
  if (idx >= 0) {
    const prev = messages.value[idx]
    messages.value.splice(idx, 1, { ...prev, pending: true, awaitingNext: false })
  }
}

/** 在消息列表中把指定 id 的消息替换为新内容（保留占位用）。 */
export function replaceMessage(
  messages: AiChatContext['messages'],
  msgId: string,
  next: AiUiMessage
): void {
  const idx = messages.value.findIndex((m) => m.id === msgId)
  if (idx >= 0) {
    if (next.timeline) {
      // 性能债修复(热点2)：同 finalizeAssistantTurn，时间线原始化。
      markRaw(next.timeline)
    }
    messages.value.splice(idx, 1, next)
  }
}

/** 在消息列表中查找指定 id 消息的索引。 */
export function findMessageIndex(
  messages: AiChatContext['messages'],
  msgId: string
): number {
  return messages.value.findIndex((m) => m.id === msgId)
}

/** 用户主动取消时的占位处理：仅关闭 pending，保留已有内容。 */
export function applyUserCancelled(
  messages: AiChatContext['messages'],
  pendingMsgId: string
): void {
  const idx = messages.value.findIndex((m) => m.id === pendingMsgId)
  if (idx >= 0) {
    const prev = messages.value[idx]
    messages.value.splice(idx, 1, { ...prev, pending: false, stoppedByUser: true })
  }
}

/** 网络错误时的错误态展示：替换为失败气泡并记录 lastError。 */
export function applyStreamError(
  messages: AiChatContext['messages'],
  lastError: AiChatContext['lastError'],
  pendingMsgId: string,
  fallbackTemplate: AiUiMessage,
  err: unknown,
  bindError = true
): void {
  const message = formatSseStreamError(err)
  if (bindError) {
    lastError.value = message
  }
  const idx = messages.value.findIndex((m) => m.id === pendingMsgId)
  if (idx >= 0) {
    messages.value.splice(idx, 1, {
      ...fallbackTemplate,
      content: '抱歉，AI 助手暂时无法响应。',
      pending: false,
      error: message
    })
  }
}

/** 未收到 done 事件时的兜底错误态。 */
export function applyMissingDone(
  messages: AiChatContext['messages'],
  lastError: AiChatContext['lastError'],
  pendingMsgId: string,
  bindError = true
): void {
  if (bindError) {
    lastError.value = '未收到服务端完成事件'
  }
  const idx = messages.value.findIndex((m) => m.id === pendingMsgId)
  if (idx >= 0) {
    const prev = messages.value[idx]
    messages.value.splice(idx, 1, {
      ...prev,
      content: '抱歉，AI 助手暂时无法响应。',
      pending: false,
      error: '未收到服务端完成事件'
    })
  }
}

/**
 * 统一的流异常分支处理：
 * - 已 doneProcessed：静默
 * - 用户主动取消：关闭 pending，保留已有内容
 * - 网络错误：替换为错误气泡
 */
export function handleStreamError(
  messages: AiChatContext['messages'],
  lastError: AiChatContext['lastError'],
  pendingMsgId: string,
  fallbackTemplate: AiUiMessage,
  streamAcc: ChatStreamAccumulator,
  abortCtrl: CancellableAbort,
  err: unknown
): void {
  if (streamAcc.doneProcessed) {
    return
  }
  const userCancelled = abortCtrl._userCancelled === true
  if (userCancelled) {
    applyUserCancelled(messages, pendingMsgId)
  } else {
    applyStreamError(messages, lastError, pendingMsgId, fallbackTemplate, err)
  }
}

/** 取消当前会话的在途流：标记 _userCancelled 并 abort，让 catch 分支按用户取消语义处理。 */
export function cancelStream(
  streamsBySession: StreamRegistry,
  sessionId: AiChatContext['sessionId']
): void {
  const ctrl = streamsBySession.get(sessionId.value)
  if (!ctrl) {
    return
  }
  try {
    // Flag the cancellation reason via a synthetic property on the controller so the
    // catch handler can branch without conflating with real network errors.
    // Don't unregister here — let the finally block (settleStream) do it
    // after the catch has had a chance to read the flag.
    ctrl._userCancelled = true
    if (ctrl._runId) {
      void cancelSseV2Stream(ctrl._runId).catch(() => {
        // The local abort still stops rendering; backend disconnect grace remains the fallback.
      })
    }
    ctrl.abort()
  } catch {
    /* noop */
  }
}

/** 创建一个新的 pending 助手消息并返回（用于 sendMessage / regenerate）。 */
export function createPendingAssistantMessage(): AiUiMessage {
  return {
    id: genMessageId(),
    role: 'assistant',
    content: '',
    pending: true,
    timeline: [],
    createdAt: Date.now()
  }
}

/** 创建一个空的流累加器。 */
export function createStreamAccumulator(): ChatStreamAccumulator {
  return {
    donePayload: null,
    doneProcessed: false,
    streamErrored: false,
    v2RunId: null,
    v2LastSeq: 0,
    ownerSessionId: null
  }
}

/** 派发 SSE 事件到 ChatStreamBridge。 */
export function dispatchEvent(
  eventName: string,
  data: unknown,
  bridge: ChatStreamBridge
): void {
  dispatchChatStreamEvent(eventName, data, bridge)
}
